// Full-page claim editor — every CMS-1500 box on one screen, in form order,
// with the 837P that will actually be sent shown beside it.
//
// Deliberately a page and not a modal: the existing Edit Claim modal covers nine
// fields and is already at the height of the viewport. This covers about forty.
//
// The whole contract is two calls:
//   GET  /api/practices/:pid/claims/:cid/edit  -> the view (every field, resolved,
//                                                 plus sent_preview and the rules verdict)
//   PUT  /api/practices/:pid/claims/:cid/edit  -> the same view, refreshed
// so the preview and the verdict chip come back from the same response that
// saved, and there is never a second round-trip where they could disagree.
//
// Editing is not submitting. Nothing on this page sends a claim anywhere.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./ClaimEditor.css";
import { SubmitToQueueButton } from "../queue";
import ReadinessChecklist, { readinessBlockReason } from "./ReadinessChecklist";
import SubmitNowButton from "./SubmitNowButton";
import { NotesPane } from "../notes";

const API_URL = process.env.REACT_APP_API_URL || "";

// Place of service codes a biller actually reaches for. Free text is still
// allowed — the server validates any two digits — so an unusual code is typed,
// not blocked.
const POS_CODES = [
  ["02", "02 — Telehealth (away from home)"],
  ["10", "10 — Telehealth (in patient home)"],
  ["11", "11 — Office"],
  ["12", "12 — Home"],
  ["19", "19 — Off-campus outpatient hospital"],
  ["21", "21 — Inpatient hospital"],
  ["22", "22 — On-campus outpatient hospital"],
  ["23", "23 — Emergency room"],
  ["31", "31 — Skilled nursing facility"],
  ["49", "49 — Independent clinic"],
  ["81", "81 — Independent laboratory"],
];

const RELATED_CAUSES = [
  ["", "None"],
  ["AA", "Auto accident"],
  ["EM", "Employment"],
  ["OA", "Other accident"],
];

const MAX_ICDS = 12; // CMS-1500 box 21 holds twelve
const MAX_POINTERS = 4; // the 837 composite pointer limit

const EMPTY_LINE = {
  code: "",
  modifiers: [],
  units: 1,
  charge: 0,
  dx_pointers: [],
};

// Turn the server's view into the flat form state the inputs bind to. Kept
// separate from render so a save can reseed the form from the response without
// any field-by-field merging.
function viewToForm(view) {
  return {
    date_of_service: view.header.date_of_service || "",
    pos: view.header.pos || "11",
    member_id: view.header.member_id || "",
    payer_name: view.header.payer_name || "",
    payer_id: view.header.payer_id || "",
    payer_sequence: view.header.payer_sequence || 1,
    recipient_type: view.header.recipient_type || "insurance",
    claim_frequency_code: view.header.claim_frequency_code || "1",
    rendering_provider_id: view.providers.rendering.id ?? "",
    billing_provider_id: view.providers.billing.id ?? "",
    referring_provider_name: view.providers.referring.name || "",
    referring_provider_npi: view.providers.referring.npi || "",
    service_facility_name: view.providers.facility.name || "",
    service_facility_npi: view.providers.facility.npi || "",
    prior_auth_number: view.auth.prior_auth_number || "",
    referral_number: view.auth.referral_number || "",
    onset_date: view.dates.onset_date || "",
    accident_date: view.dates.accident_date || "",
    accident_state: view.dates.accident_state || "",
    related_causes_code: view.dates.related_causes_code || "",
    admission_date: view.dates.admission_date || "",
    discharge_date: view.dates.discharge_date || "",
    initial_treatment_date: view.dates.initial_treatment_date || "",
    icd_codes: [...view.icd_codes],
    lines: view.lines.map((line) => ({
      code: line.code || "",
      modifiers: [...(line.modifiers || [])],
      units: line.units ?? 1,
      // null means the line was never priced (charge_coding_service only prices
      // a CPT it finds in the fee schedule). An empty box, never a 0 — see
      // formToPayload, which then sends nothing back for it.
      charge: line.charge ?? "",
      // Only pointers the biller actually chose travel back. A line showing the
      // all-diagnoses default sends none, so the default keeps applying at
      // submit time instead of being frozen by the act of saving.
      dx_pointers: line.dx_pointers_explicit ? [...line.dx_pointers] : [],
      // Advisory, from the fee schedule. Never sent back — the server derives it
      // and the editor must not let a stale copy become a claim's price.
      fee: line.fee || null,
    })),
  };
}

// Both provider ids come back under `providers` (resolved, with names) and go
// out as plain claim columns. A select's value is always a string, so they are
// coerced back to numbers here rather than letting "5" reach the server.
function formToPayload(changed, form) {
  // `lines` and `icd_codes` always travel together when either moved: the
  // server validates each line's diagnosis pointers against the claim's
  // diagnosis list, so sending one without the other would check the new
  // pointers against the old list.
  if ("lines" in changed || "icd_codes" in changed) {
    changed = { ...changed, lines: form.lines, icd_codes: form.icd_codes };
  }
  const out = {
    ...changed,
    rendering_provider_id:
      form.rendering_provider_id === ""
        ? null
        : Number(form.rendering_provider_id),
    billing_provider_id:
      form.billing_provider_id === "" ? null : Number(form.billing_provider_id),
    payer_sequence: Number(form.payer_sequence) || 1,
    lines: (changed.lines || []).map((line) => {
      // `fee` is display-only and deliberately absent from the payload: it is
      // the fee schedule's opinion, not the claim's price, and the edit
      // whitelist would reject it.
      const entry = {
        code: line.code.trim(),
        modifiers: line.modifiers.filter((m) => m && m.trim()),
        units: Number(line.units) || 1,
      };
      // An empty charge box means "leave it as it was", so send nothing. A
      // typed 0 is a real zero and is sent. `Number(x) || 0` would have
      // collapsed the two and zeroed the claim's billed amount on a save that
      // was about something else.
      if (line.charge !== "" && line.charge !== null) {
        entry.charge = Number(line.charge) || 0;
      }
      if (line.dx_pointers.length) entry.dx_pointers = line.dx_pointers;
      return entry;
    }),
    icd_codes: (changed.icd_codes || []).filter((c) => c && c.trim()),
  };
  // Drop the keys the coercions above added for fields that did not change.
  Object.keys(out).forEach((key) => {
    if (!(key in changed)) delete out[key];
  });
  return out;
}

// Only the fields the biller actually changed. A whole-form PUT would stamp the
// form's own defaults onto boxes nobody touched — `recipient_type` defaults to
// "insurance" here, but the column is legitimately NULL on older claims and
// carries an explicit billing INTENT, so a save about the member id would have
// silently decided who the claim bills to. It also keeps an edit from writing
// `pos` onto every claim that never had one.
function changedFields(form, original) {
  const out = {};
  Object.keys(form).forEach((key) => {
    if (JSON.stringify(form[key]) !== JSON.stringify(original[key])) {
      out[key] = form[key];
    }
  });
  return out;
}

// A section that opens and closes. Same `.ce-section` box and the same heading as the
// sections that do not collapse — the change is order and disclosure, nothing visual
// (David has not set a design direction yet).
function Collapsible({ title, sectionKey, open, onToggle, children }) {
  const isOpen = !!open[sectionKey];
  return (
    <section className="ce-section">
      <h2>
        <button
          type="button"
          className="ce-collapse-toggle"
          aria-expanded={isOpen}
          onClick={() => onToggle(sectionKey)}
        >
          <span aria-hidden="true">{isOpen ? "\u25be" : "\u25b8"}</span> {title}
        </button>
      </h2>
      {isOpen && children}
    </section>
  );
}

function VerdictChip({ verdict }) {
  if (!verdict)
    return <span className="ce-chip ce-chip-none">Not evaluated</span>;
  return <span className={`ce-chip ce-chip-${verdict}`}>{verdict}</span>;
}

export default function ClaimEditor({
  selectedPractice,
  claimId,
  onClose,
  onNotFound,
  readOnly = false,
  isAdmin = false,
  features = {},
  // The ledger opens this in a drawer whose backdrop closes it. It cannot know whether
  // there is typing to lose unless this says so.
  onDirtyChange,
}) {
  const practiceId = selectedPractice?.id;
  const [view, setView] = useState(null);
  const [form, setForm] = useState(null);
  // The form as it was loaded, so a save can send only what moved.
  const [original, setOriginal] = useState(null);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  // Which collapsible sections are open. Per view, not per user: a section opens
  // because a readiness row inside it is red, which is the thing that needs looking at.
  const [open, setOpen] = useState({});

  const toggle = useCallback((key) => {
    setOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const set = useCallback((field, value) => {
    setSaved(false);
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const onNotFoundRef = useRef(onNotFound);
  onNotFoundRef.current = onNotFound;
  // Which load is current. A practice switch re-renders this editor with the new practice
  // and the old claim for one pass before the page resets it; that load's late 404 must
  // not report "not found" about a claim nobody is looking at any more.
  const loadSeq = useRef(0);
  useEffect(
    () => () => {
      loadSeq.current += 1;
    },
    [],
  );

  const load = useCallback(async () => {
    if (!practiceId || !claimId) return;
    const mine = ++loadSeq.current;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/claims/${claimId}/edit`,
        { credentials: "include" },
      );
      if (mine !== loadSeq.current) return;
      // Read through a ref: a parent re-rendering with a new function must not refetch.
      if (response.status === 404 && onNotFoundRef.current) {
        onNotFoundRef.current(claimId);
        return;
      }
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || `Could not load claim ${claimId}`);
      }
      const data = await response.json();
      const seeded = viewToForm(data);
      setView(data);
      setForm(seeded);
      setOriginal(seeded);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [practiceId, claimId]);

  useEffect(() => {
    load();
  }, [load]);

  // Open the sections a red readiness row points at, and leave the rest closed. Seeded
  // once per claim, from the FIRST view that arrives — seeding while the view is still
  // null would write "closed" for every section and then never reopen them — and a
  // biller's own open/close survives every later save.
  const seededFor = useRef(null);
  useEffect(() => {
    if (!view || seededFor.current === view.claim_id) return;
    seededFor.current = view.claim_id;
    const red = new Set(
      (view.readiness?.rows || []).filter((row) => !row.ok).map((row) => row.section),
    );
    setOpen({
      claim: red.has("claim"),
      insurance: red.has("insurance"),
      providers: red.has("providers") || red.has("patient"),
      auth: false,
      dates: false,
      advanced: false,
    });
  }, [view]);

  // The rendering/billing provider pickers. Same source the ledger's modal uses.
  useEffect(() => {
    if (!practiceId) return undefined;
    let cancelled = false;
    fetch(`${API_URL}/api/practices/${practiceId}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.providers && !cancelled) setProviders(data.providers);
      })
      .catch(() => {
        /* the pickers fall back to whatever the claim already has */
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId]);

  const save = async () => {
    const changed = changedFields(form, original);
    if (!Object.keys(changed).length) {
      setSaved(true);
      return;
    }
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/claims/${claimId}/edit`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(formToPayload(changed, form)),
        },
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(body.error || "Could not save the claim");
      const seeded = viewToForm(body);
      setView(body);
      setForm(seeded);
      setOriginal(seeded);
      setSaved(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // --- line + diagnosis helpers ---------------------------------------------

  const setLine = (index, patch) => {
    setSaved(false);
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.map((line, i) =>
        i === index ? { ...line, ...patch } : line,
      ),
    }));
  };

  const togglePointer = (index, pointer) => {
    const line = form.lines[index];
    const has = line.dx_pointers.includes(pointer);
    if (!has && line.dx_pointers.length >= MAX_POINTERS) return;
    setLine(index, {
      dx_pointers: has
        ? line.dx_pointers.filter((p) => p !== pointer)
        : [...line.dx_pointers, pointer].sort((a, b) => a - b),
    });
  };

  // Diagnosis ORDER is the pointer order — moving a code renumbers every pointer
  // that referenced it, so the pointers move with it rather than silently coming
  // to mean a different diagnosis.
  const moveIcd = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= form.icd_codes.length) return;
    setSaved(false);
    setForm((prev) => {
      const icds = [...prev.icd_codes];
      [icds[index], icds[target]] = [icds[target], icds[index]];
      const from = index + 1;
      const to = target + 1;
      const remap = (p) => (p === from ? to : p === to ? from : p);
      return {
        ...prev,
        icd_codes: icds,
        lines: prev.lines.map((line) => ({
          ...line,
          dx_pointers: line.dx_pointers.map(remap).sort((a, b) => a - b),
        })),
      };
    });
  };

  const removeIcd = (index) => {
    setSaved(false);
    setForm((prev) => {
      const removed = index + 1;
      return {
        ...prev,
        icd_codes: prev.icd_codes.filter((_, i) => i !== index),
        // Drop pointers at the removed position and shift the ones above it
        // down, so no line is left pointing past the end of the list.
        lines: prev.lines.map((line) => ({
          ...line,
          dx_pointers: line.dx_pointers
            .filter((p) => p !== removed)
            .map((p) => (p > removed ? p - 1 : p)),
        })),
      };
    });
  };

  const dirty = !!(form && original && Object.keys(changedFields(form, original)).length);
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  const total = useMemo(() => {
    if (!form) return 0;
    return form.lines.reduce(
      (sum, line) =>
        sum + (Number(line.charge) || 0) * (Number(line.units) || 1),
      0,
    );
  }, [form]);

  if (loading)
    return (
      <div className="claim-editor-page">
        <div className="ce-loading">Loading claim…</div>
      </div>
    );
  if (error && !view) {
    return (
      <div className="claim-editor-page">
        <div className="form-message error">{error}</div>
        <button className="btn btn-secondary" onClick={onClose}>
          Back to claims
        </button>
      </div>
    );
  }
  if (!view || !form) return null;

  const locked = readOnly || !view.editable;
  const preview = view.sent_preview;
  // What the 837 would carry for the facility if the boxes stay empty
  // (claim_defaults_service.resolve). Shown, never written on its own.
  const defaults = view.defaults || {};
  const facilityDefault = {
    name: defaults.service_facility_name?.on_claim
      ? ""
      : defaults.service_facility_name?.value || "",
    npi: defaults.service_facility_npi?.on_claim
      ? ""
      : defaults.service_facility_npi?.value || "",
  };
  // What the 837 will ACTUALLY carry. build_snapshot_fields prefers the
  // patient's Coverage row over the claim's own member_id column, so the box
  // above can disagree with the wire — and a biller fixing a member-ID
  // rejection needs to know that before they resubmit the same number.
  const wireMemberId = preview.payload?.subscriber?.memberId;

  return (
    <div className="claim-editor-page">
      <div className="ce-header">
        <div>
          <h1 className="page-title">Edit claim #{view.claim_id}</h1>
          <div className="ce-subtitle">
            {view.header.patient_name || "No patient linked"}
            {(view.header.patient_payment_type || "").toLowerCase() ===
              "self_pay" && <span className="ce-edited">Cash pay</span>}
            {" · "}
            <span className={`ce-status ce-status-${view.claim_status}`}>
              {view.claim_status}
            </span>
            {view.raw_claim_status && (
              <span
                className="ce-raw-status"
                title="The status word or code the source system gave this claim"
              >
                source: {view.raw_claim_status}
              </span>
            )}
            {view.edited_in_atlas && (
              <span className="ce-edited">edited in Atlas</span>
            )}
          </div>
        </div>
        <div className="ce-header-actions">
          <VerdictChip verdict={view.rules.verdict} />
          <SubmitNowButton
            practiceId={practiceId}
            practice={selectedPractice}
            features={features}
            claim={{
              id: view.claim_id,
              claim_status: view.claim_status,
              payer_name: view.header.payer_name,
            }}
            readiness={view.readiness}
            readOnly={readOnly}
            dirty={
              saving || Object.keys(changedFields(form, original)).length > 0
            }
            onDone={load}
          />
          <SubmitToQueueButton
            practiceId={practiceId}
            claim={{ id: view.claim_id, claim_status: view.claim_status }}
            readOnly={readOnly}
            dirty={
              saving || Object.keys(changedFields(form, original)).length > 0
            }
            blockedReason={readinessBlockReason(view.readiness)}
            // One claim on screen, and room to say which portal it is going to.
            showDestination
          />
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          <button
            className="btn btn-primary"
            onClick={save}
            disabled={locked || saving}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {!view.editable && (
        <div className="form-message warn">{view.refusal_reason}</div>
      )}
      {error && <div className="form-message error">{error}</div>}
      {saved && <div className="form-message success">Saved.</div>}

      {view.editable && (
        <ReadinessChecklist readiness={view.readiness} practiceId={practiceId} />
      )}
      <NotesPane practiceId={practiceId} claimId={claimId} features={features} readOnly={readOnly} canEdit={!locked} claimIcds={form.icd_codes} claimLines={form.lines} onAcceptDiagnosis={(code) => set("icd_codes", [...form.icd_codes, code])} onAcceptProcedure={(line) => set("lines", [...form.lines, { ...EMPTY_LINE, ...line, charge: "" }])} />

      <div className="ce-body ce-body-single">
        <div className="ce-form">
          <fieldset disabled={locked}>
            {/* Diagnosis and the service lines first — the two things a biller
                actually changes (David's walk, 2026-09-15). Everything else is
                below, collapsed, and opens itself when a readiness row in it is red. */}
            <section className="ce-section">
              <h2>Diagnoses (21)</h2>
              <p className="ce-hint">
                Order is pointer order: the first code is pointer 1.
              </p>
              <ol className="ce-icd-list">
                {form.icd_codes.map((code, i) => (
                  <li key={i} className="ce-icd-row">
                    <span className="ce-icd-index">{i + 1}</span>
                    <input
                      value={code}
                      onChange={(e) => {
                        const icds = [...form.icd_codes];
                        icds[i] = e.target.value.toUpperCase();
                        set("icd_codes", icds);
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => moveIcd(i, -1)}
                      disabled={i === 0}
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => moveIcd(i, 1)}
                      disabled={i === form.icd_codes.length - 1}
                      title="Move down"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => removeIcd(i)}
                      title="Remove"
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ol>
              {form.icd_codes.length < MAX_ICDS && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => set("icd_codes", [...form.icd_codes, ""])}
                >
                  Add diagnosis
                </button>
              )}
            </section>

            <section className="ce-section">
              <h2>Service lines (24)</h2>
              <div className="ce-line-scroll">
                <table className="ce-lines">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>CPT</th>
                      <th>Modifiers</th>
                      <th>Units</th>
                      <th>Charge</th>
                      <th>Pointers (24E)</th>
                      <th>Line total</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {form.lines.map((line, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td>
                          <input
                            className="ce-cpt"
                            value={line.code}
                            onChange={(e) =>
                              setLine(i, { code: e.target.value.toUpperCase() })
                            }
                          />
                        </td>
                        <td className="ce-mods">
                          {[0, 1, 2, 3].map((m) => (
                            <input
                              key={m}
                              maxLength={2}
                              value={line.modifiers[m] || ""}
                              onChange={(e) => {
                                const mods = [...line.modifiers];
                                while (mods.length < 4) mods.push("");
                                mods[m] = e.target.value.toUpperCase();
                                setLine(i, { modifiers: mods });
                              }}
                            />
                          ))}
                        </td>
                        <td>
                          <input
                            type="number"
                            min="1"
                            className="ce-num"
                            value={line.units}
                            onChange={(e) =>
                              setLine(i, { units: e.target.value })
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="ce-num"
                            value={line.charge}
                            onChange={(e) =>
                              setLine(i, { charge: e.target.value })
                            }
                          />
                          {line.fee && (
                            <div
                              className="ce-fee"
                              title="From the practice's fee schedule"
                            >
                              {line.fee.charged_amount != null && (
                                <span>
                                  fee ${line.fee.charged_amount.toFixed(2)}
                                </span>
                              )}
                              {line.fee.contracted_rate != null && (
                                <span>
                                  {" "}
                                  · contracted $
                                  {line.fee.contracted_rate.toFixed(2)}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="ce-pointers">
                          {form.icd_codes.map((_, d) => (
                            <label key={d} title={form.icd_codes[d]}>
                              <input
                                type="checkbox"
                                checked={line.dx_pointers.includes(d + 1)}
                                onChange={() => togglePointer(i, d + 1)}
                              />
                              {d + 1}
                            </label>
                          ))}
                          {!line.dx_pointers.length && <em>all</em>}
                        </td>
                        <td className="ce-line-total">
                          $
                          {(
                            (Number(line.charge) || 0) *
                            (Number(line.units) || 1)
                          ).toFixed(2)}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            disabled={form.lines.length === 1}
                            onClick={() =>
                              set(
                                "lines",
                                form.lines.filter((_, x) => x !== i),
                              )
                            }
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={6} />
                      <td className="ce-line-total">${total.toFixed(2)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => set("lines", [...form.lines, { ...EMPTY_LINE }])}
              >
                Add line
              </button>
            </section>
            <Collapsible title="Claim" sectionKey="claim" open={open} onToggle={toggle}>
              <div className="ce-row">
                <label className="ce-field">
                  <span>Date of service</span>
                  <input
                    type="date"
                    value={form.date_of_service}
                    onChange={(e) => set("date_of_service", e.target.value)}
                  />
                </label>
                <label className="ce-field">
                  <span>Place of service (24B)</span>
                  <input
                    list="ce-pos-codes"
                    value={form.pos}
                    onChange={(e) => set("pos", e.target.value)}
                    maxLength={2}
                  />
                  <datalist id="ce-pos-codes">
                    {POS_CODES.map(([code, label]) => (
                      <option key={code} value={code}>
                        {label}
                      </option>
                    ))}
                  </datalist>
                </label>
                <label className="ce-field">
                  <span>Bill to</span>
                  <select
                    value={form.recipient_type}
                    onChange={(e) => set("recipient_type", e.target.value)}
                  >
                    <option value="insurance">Insurance</option>
                    <option value="patient">Patient</option>
                  </select>
                </label>
                <label className="ce-field">
                  <span>Frequency (22)</span>
                  <select
                    value={form.claim_frequency_code}
                    onChange={(e) =>
                      set("claim_frequency_code", e.target.value)
                    }
                  >
                    <option value="1">1 — Original</option>
                    <option value="7" disabled={!view.header.parent_claim_id}>
                      7 — Replacement
                    </option>
                    <option value="8" disabled={!view.header.parent_claim_id}>
                      8 — Void
                    </option>
                  </select>
                  {!view.header.parent_claim_id && (
                    <small>
                      Use “Create corrected claim” on the ledger — a 7 or 8
                      needs the payer’s claim number from the original.
                    </small>
                  )}
                </label>
              </div>
            </Collapsible>
            <Collapsible title="Insurance" sectionKey="insurance" open={open} onToggle={toggle}>
              <div className="ce-row">
                <label className="ce-field">
                  <span>Payer name</span>
                  <input
                    value={form.payer_name}
                    onChange={(e) => set("payer_name", e.target.value)}
                  />
                </label>
                <label className="ce-field">
                  <span>Payer ID</span>
                  <input
                    value={form.payer_id}
                    onChange={(e) => set("payer_id", e.target.value)}
                  />
                </label>
                <label className="ce-field">
                  <span>Member ID (1a)</span>
                  <input
                    value={form.member_id}
                    onChange={(e) => set("member_id", e.target.value)}
                  />
                  {wireMemberId ? (
                    wireMemberId !== view.header.member_id && (
                      <small className="ce-warn">
                        The 837 will carry {wireMemberId}, from the
                        patient&rsquo;s insurance card. Changing it here does
                        not change the card.
                      </small>
                    )
                  ) : (
                    // No preview to read the real value from — which is exactly
                    // when a biller is fixing a broken claim, so say it anyway.
                    <small className="ce-warn">
                      The 837 takes the member ID from the patient&rsquo;s
                      insurance card when there is one, not from this box.
                    </small>
                  )}
                </label>
                <label className="ce-field">
                  <span>Sequence</span>
                  <select
                    value={form.payer_sequence}
                    onChange={(e) => set("payer_sequence", e.target.value)}
                  >
                    <option value={1}>Primary</option>
                    <option value={2}>Secondary</option>
                    <option value={3}>Tertiary</option>
                  </select>
                </label>
              </div>
            </Collapsible>
            <Collapsible title="Providers & facility" sectionKey="providers" open={open} onToggle={toggle}>
              <div className="ce-row">
                <label className="ce-field">
                  <span>Rendering (24J)</span>
                  <select
                    value={form.rendering_provider_id}
                    onChange={(e) =>
                      set("rendering_provider_id", e.target.value)
                    }
                  >
                    <option value="">— none —</option>
                    {providers.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="ce-field">
                  <span>Billing (33)</span>
                  <select
                    value={form.billing_provider_id}
                    onChange={(e) => set("billing_provider_id", e.target.value)}
                  >
                    <option value="">— the practice —</option>
                    {providers.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="ce-field">
                  <span>Referring (17)</span>
                  <input
                    value={form.referring_provider_name}
                    placeholder="Name"
                    onChange={(e) =>
                      set("referring_provider_name", e.target.value)
                    }
                  />
                </label>
                <label className="ce-field">
                  <span>Referring NPI (17b)</span>
                  <input
                    value={form.referring_provider_npi}
                    maxLength={10}
                    placeholder="10 digits"
                    onChange={(e) =>
                      set("referring_provider_npi", e.target.value)
                    }
                  />
                  {form.referring_provider_name &&
                    !form.referring_provider_npi && (
                      <small className="ce-warn">
                        Without an NPI the referring provider is not sent.
                      </small>
                    )}
                </label>
              </div>
              <div className="ce-row">
                <label className="ce-field">
                  <span>Service facility (32)</span>
                  <input
                    value={form.service_facility_name}
                    placeholder={facilityDefault.name || ""}
                    onChange={(e) =>
                      set("service_facility_name", e.target.value)
                    }
                  />
                  {/* A blank box is not a blank claim: with no facility the 837 carries
                      no facility loop and the payer reads the billing address, which is
                      the practice's. Said out loud rather than stamped onto the claim —
                      stamping would add a loop to a draft nobody edited. */}
                  {!form.service_facility_name && (
                    <small>
                      {facilityDefault.name
                        ? `${facilityDefault.name} — practice address (what the 837 will carry)`
                        : "The practice's address is what the 837 will carry."}
                    </small>
                  )}
                </label>
                <label className="ce-field">
                  <span>Facility NPI (32a)</span>
                  <input
                    value={form.service_facility_npi}
                    maxLength={10}
                    placeholder={facilityDefault.npi || ""}
                    onChange={(e) =>
                      set("service_facility_npi", e.target.value)
                    }
                  />
                </label>
                {(facilityDefault.name || facilityDefault.npi) &&
                  !form.service_facility_name &&
                  !form.service_facility_npi && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => {
                        setSaved(false);
                        setForm((prev) => ({
                          ...prev,
                          service_facility_name: facilityDefault.name || "",
                          service_facility_npi: facilityDefault.npi || "",
                        }));
                      }}
                    >
                      Use the practice
                    </button>
                  )}
              </div>
            </Collapsible>
            <Collapsible title="Authorization" sectionKey="auth" open={open} onToggle={toggle}>
              <div className="ce-row">
                <label className="ce-field">
                  <span>Prior authorization (23)</span>
                  <input
                    value={form.prior_auth_number}
                    onChange={(e) => set("prior_auth_number", e.target.value)}
                  />
                </label>
                <label className="ce-field">
                  <span>Referral number</span>
                  <input
                    value={form.referral_number}
                    onChange={(e) => set("referral_number", e.target.value)}
                  />
                </label>
              </div>
            </Collapsible>
            <Collapsible title="Dates & condition" sectionKey="dates" open={open} onToggle={toggle}>
              <div className="ce-row">
                <label className="ce-field">
                  <span>Onset of illness (14)</span>
                  <input
                    type="date"
                    value={form.onset_date}
                    onChange={(e) => set("onset_date", e.target.value)}
                  />
                </label>
                <label className="ce-field">
                  <span>Initial treatment</span>
                  <input
                    type="date"
                    value={form.initial_treatment_date}
                    onChange={(e) =>
                      set("initial_treatment_date", e.target.value)
                    }
                  />
                </label>
                <label className="ce-field">
                  <span>Admission (18)</span>
                  <input
                    type="date"
                    value={form.admission_date}
                    onChange={(e) => set("admission_date", e.target.value)}
                  />
                </label>
                <label className="ce-field">
                  <span>Discharge (18)</span>
                  <input
                    type="date"
                    value={form.discharge_date}
                    onChange={(e) => set("discharge_date", e.target.value)}
                  />
                </label>
              </div>
              <div className="ce-row">
                <label className="ce-field">
                  <span>Related cause (10)</span>
                  <select
                    value={form.related_causes_code}
                    onChange={(e) => set("related_causes_code", e.target.value)}
                  >
                    {RELATED_CAUSES.map(([code, label]) => (
                      <option key={code} value={code}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                {/* Both only mean anything alongside a cause, so they appear with it. */}
                {form.related_causes_code && (
                  <label className="ce-field">
                    <span>Accident date (15)</span>
                    <input
                      type="date"
                      value={form.accident_date}
                      onChange={(e) => set("accident_date", e.target.value)}
                    />
                  </label>
                )}
                {form.related_causes_code === "AA" && (
                  <label className="ce-field">
                    <span>Accident state</span>
                    <input
                      value={form.accident_state}
                      maxLength={2}
                      placeholder="CA"
                      onChange={(e) =>
                        set("accident_state", e.target.value.toUpperCase())
                      }
                    />
                  </label>
                )}
              </div>
            </Collapsible>
          </fieldset>

          {/* What the rules engine found is the biller's business and stays in front of
              everyone. WHY a claim will not build is on the readiness checklist at the
              top (the `builds` row, carrying the builder's own codes); the 837 payload
              itself, and the raw build error beside it, are ours — collapsed, at the
              bottom, admin only (David: "837 preview — just for us?" — yes). */}
          {view.rules.detail.length > 0 && (
            <section className="ce-section">
              <h2>Billing rules</h2>
              <ul className="ce-rules">
                {view.rules.detail.map((d, i) => (
                  <li key={i}>
                    <strong>{d.rule_key}</strong> {d.reason}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {isAdmin && (
            <Collapsible title="Advanced" sectionKey="advanced" open={open} onToggle={toggle}>
              <h3 className="ce-advanced-title">As it will be sent</h3>
              <p className="ce-hint">
                The 837P for this claim as saved. Unsaved edits are not reflected
                until you save.
              </p>
              {preview.error ? (
                <div className="form-message warn">{preview.error}</div>
              ) : (
                <pre className="ce-json">
                  {JSON.stringify(preview.payload, null, 2)}
                </pre>
              )}
            </Collapsible>
          )}
        </div>

      </div>
    </div>
  );
}
